import React from 'react';
import classNames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';

import { ReactComponent as CheckmarkIcon } from '@assets/images/checkmark.svg';
import { TimelineGroup } from '@customTypes/timeline';
import { getStatusFlags } from '../utils/getStatusFlags';

const TOOLTIP_CONFIG = {
  delay: { show: 100, hide: 0 },
  popperConfig: {
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          boundary: 'viewport',
          padding: 8,
        },
      },
      {
        name: 'computeStyles',
        options: {
          gpuAcceleration: false,
        },
      },
    ],
  },
};

interface TimelineClusterProps {
  group: TimelineGroup;
  index: number;
  groups: TimelineGroup[];
  lastProgressIndex: number | null;
  tooltipPlacement: 'top' | 'right' | 'bottom' | 'left';
}

export default function TimelineCluster({
  group,
  index,
  groups,
  lastProgressIndex,
  tooltipPlacement,
}: TimelineClusterProps) {
  const isFirst = index === 0;
  const isLast = index === groups.length - 1;
  const { isCompleted, isInProgress, hasProgress } = getStatusFlags(group.status);

  const nextGroup = !isLast ? groups[index + 1] : undefined;
  const nextCompleted = nextGroup?.status === 'complete';
  const nextHasProgress = !!nextGroup && (nextGroup.status === 'complete' || nextGroup.status === 'in-progress');

  const isLastCompletedSegment = lastProgressIndex !== null && index === lastProgressIndex && !isLast;
  const shouldRemoveBorderRadius = isCompleted && nextHasProgress;
  const isFollowedByCompleted = isCompleted && !!nextCompleted;

  const clusterContent = (
    <div className='timeline__cluster'>
      {/* Left line segment: extends from cluster center to midpoint with previous cluster */}
      {/* Blue if current cluster has any progress (complete or in-progress), which implies previous is completed */}
      {!isFirst && (
        <span
          className={classNames('timeline__cluster-segment', 'timeline__cluster-segment--left', {
            'timeline__cluster-segment--completed': hasProgress,
          })}
        />
      )}
      {/* Right line segment: extends from cluster center to midpoint with next cluster */}
      {!isLast && (
        <span
          className={classNames('timeline__cluster-segment', 'timeline__cluster-segment--right', {
            'timeline__cluster-segment--completed': isCompleted,
            'timeline__cluster-segment--right--last-completed': isLastCompletedSegment && !shouldRemoveBorderRadius,
            'timeline__cluster-segment--right--followed-by-completed':
              isFollowedByCompleted || shouldRemoveBorderRadius,
          })}
        />
      )}

      <div className='dot-container'>
        <div
          className={classNames(
            'timeline__dot',
            isCompleted
              ? 'timeline__dot--completed'
              : isInProgress
                ? 'timeline__dot--in-progress'
                : 'timeline__dot--pending',
          )}
        >
          {isInProgress && <FontAwesomeIcon icon={faEllipsis} className='timeline__dot--ellipsis' />}
          {isCompleted && <CheckmarkIcon />}
        </div>
      </div>
    </div>
  );

  if (!group.tooltipText) {
    return clusterContent;
  }

  return (
    <OverlayTrigger
      placement={tooltipPlacement}
      overlay={<Tooltip className='timeline-tooltip'>{group.tooltipText}</Tooltip>}
      {...TOOLTIP_CONFIG}
    >
      {clusterContent}
    </OverlayTrigger>
  );
}
